/**
 * ABTestPanel — Sprint 6 · #18 A/B-test engine op de Sales Outreach-pagina.
 * Eén lopend experiment per workspace: twee stijlvarianten die om-en-om aan
 * nieuwe AI-drafts worden toegewezen. Live reply-rates per variant; afsluiten
 * met winnaar-keuze (optioneel meteen als geleerde stijlregel toepassen).
 * Data: GET/POST /ab/experiments · POST /ab/experiments/{id}/conclude
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { FlaskConical, ChevronDown, ChevronUp, Loader2, Trophy, Sparkles, X } from "lucide-react";

function RateBar({ rate, color }) {
    return (
        <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round((rate || 0) * 100))}%`, background: color }} />
        </div>
    );
}

function VariantCard({ v, stats, letter, color, running, onPickWinner, busy }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3" data-testid={`ab-variant-${letter}`}>
            <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold text-black" style={{ background: color }}>{letter.toUpperCase()}</span>
                <span className="text-xs font-medium text-white truncate">{v?.label || "?"}</span>
                <span className="ml-auto text-[10px] font-mono text-[var(--jr-text-3)]">{Math.round((stats?.reply_rate || 0) * 100)}%</span>
            </div>
            <p className="mt-1.5 text-[11px] text-[var(--jr-text-3)] leading-relaxed line-clamp-2">{v?.instruction}</p>
            <div className="mt-2"><RateBar rate={stats?.reply_rate} color={color} /></div>
            <div className="mt-1.5 flex items-center gap-3 text-[10px] font-mono text-[var(--jr-text-3)]">
                <span>{stats?.drafts || 0} {i18n.t("ab.drafts")}</span>
                <span>{stats?.sent || 0} {i18n.t("ab.sent")}</span>
                <span>{stats?.replied || 0} {i18n.t("ab.replied")}</span>
                <span title={i18n.t("ab.positive")}>+{stats?.positive || 0}</span>
            </div>
            {running && (
                <button onClick={onPickWinner} disabled={busy} data-testid={`ab-pick-${letter}`}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-white/12 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                    <Trophy size={11} /> {i18n.t("ab.pick_winner")}
                </button>
            )}
        </div>
    );
}

export default function ABTestPanel() {
    const [open, setOpen] = useState(false);
    const [experiments, setExperiments] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [busy, setBusy] = useState(false);
    const [applyTone, setApplyTone] = useState(true);
    // create form
    const [name, setName] = useState("");
    const [va, setVa] = useState(null);
    const [vb, setVb] = useState(null);

    const load = async () => {
        try {
            const [e, s] = await Promise.all([api.get("/ab/experiments"), api.get("/ab/suggestions")]);
            setExperiments(e.data.experiments || []);
            const sug = s.data.variants || [];
            setSuggestions(sug);
            setVa((v) => v || sug[0] || { label: "", instruction: "" });
            setVb((v) => v || sug[1] || { label: "", instruction: "" });
        } catch { /* panel is non-critical */ }
    };
    useEffect(() => { load(); }, []);

    if (!experiments) return null;
    const running = experiments.find((e) => e.status === "running");
    const history = experiments.filter((e) => e.status !== "running").slice(0, 3);

    const create = async () => {
        if (!name.trim() || !va?.instruction || !vb?.instruction) { toast.error(i18n.t("ab.form_incomplete")); return; }
        setBusy(true);
        try {
            await api.post("/ab/experiments", { name: name.trim(), variant_a: va, variant_b: vb });
            toast.success(i18n.t("ab.started"));
            setName("");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
        finally { setBusy(false); }
    };
    const conclude = async (winner) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/ab/experiments/${running.id}/conclude`, { winner, apply_to_tone: applyTone });
            toast.success(i18n.t("ab.concluded", { w: winner.toUpperCase() }) + (data.applied_to_tone ? ` — ${i18n.t("ab.applied_tone")}` : ""));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
        finally { setBusy(false); }
    };
    const cancel = async () => {
        setBusy(true);
        try { await api.delete(`/ab/experiments/${running.id}`); toast.success(i18n.t("ab.cancelled")); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
        finally { setBusy(false); }
    };

    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[var(--jr-surface)]" data-testid="ab-test-panel">
            <button onClick={() => setOpen(!open)} data-testid="ab-toggle"
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <FlaskConical size={15} className="text-[#c084fc]" />
                <span className="text-sm font-medium text-white">{i18n.t("ab.title")}</span>
                {running ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#c084fc]/40 text-[#c084fc] font-mono" data-testid="ab-running-chip">
                        {i18n.t("ab.running")}: {running.name}
                    </span>
                ) : (
                    <span className="text-[10px] text-[var(--jr-text-3)]">{i18n.t("ab.none_running")}</span>
                )}
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="px-4 pb-4">
                    {running ? (
                        <>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <VariantCard v={running.variant_a} stats={running.stats?.a} letter="a" color="#c084fc" running busy={busy} onPickWinner={() => conclude("a")} />
                                <VariantCard v={running.variant_b} stats={running.stats?.b} letter="b" color="#22d3ee" running busy={busy} onPickWinner={() => conclude("b")} />
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                                <label className="inline-flex items-center gap-2 text-[11px] text-[var(--jr-text-2)] cursor-pointer">
                                    <input type="checkbox" checked={applyTone} onChange={(e) => setApplyTone(e.target.checked)} data-testid="ab-apply-tone" className="accent-[#c084fc]" />
                                    <Sparkles size={11} className="text-[#c084fc]" /> {i18n.t("ab.apply_tone")}
                                </label>
                                <button onClick={cancel} disabled={busy} data-testid="ab-cancel"
                                    className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border border-white/10 text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:border-[var(--jr-error)]/40 disabled:opacity-50">
                                    <X size={10} /> {i18n.t("ab.cancel_exp")}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div data-testid="ab-create-form">
                            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="ab-name"
                                placeholder={i18n.t("ab.name_placeholder")}
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#c084fc] outline-none" />
                            <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                {[{ key: "a", v: va, set: setVa, color: "#c084fc" }, { key: "b", v: vb, set: setVb, color: "#22d3ee" }].map(({ key, v, set, color }) => (
                                    <div key={key} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold text-black" style={{ background: color }}>{key.toUpperCase()}</span>
                                            <select value={v?.label || ""} data-testid={`ab-select-${key}`}
                                                onChange={(e) => { const s = suggestions.find((x) => x.label === e.target.value); if (s) set({ ...s }); }}
                                                className="flex-1 bg-transparent border border-white/10 rounded-md px-2 py-1 text-xs text-white outline-none">
                                                {suggestions.map((s) => <option key={s.label} value={s.label}>{s.label}</option>)}
                                            </select>
                                        </div>
                                        <textarea value={v?.instruction || ""} onChange={(e) => set({ ...v, instruction: e.target.value })}
                                            rows={3} data-testid={`ab-instruction-${key}`}
                                            className="w-full bg-black/20 border border-white/10 rounded-md px-2.5 py-1.5 text-[11px] text-[var(--jr-text-2)] leading-relaxed outline-none focus:border-white/25 resize-none" />
                                    </div>
                                ))}
                            </div>
                            <button onClick={create} disabled={busy} data-testid="ab-start"
                                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#c084fc] text-black text-xs font-medium hover:opacity-90 disabled:opacity-50">
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />} {i18n.t("ab.start")}
                            </button>
                        </div>
                    )}
                    {history.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-white/8" data-testid="ab-history">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{i18n.t("ab.history")}</div>
                            {history.map((e) => (
                                <div key={e.id} className="flex items-center gap-2 text-[11px] text-[var(--jr-text-2)] py-1">
                                    <span className="truncate">{e.name}</span>
                                    {e.winner && <span className="inline-flex items-center gap-1 text-[#22c55e]"><Trophy size={10} /> {i18n.t("ab.winner")} {e.winner.toUpperCase()}</span>}
                                    {e.applied_to_tone && <span className="text-[#c084fc] inline-flex items-center gap-1"><Sparkles size={10} /> {i18n.t("ab.applied_tone")}</span>}
                                    <span className="ml-auto font-mono text-[var(--jr-text-3)]">
                                        A {Math.round((e.stats?.a?.reply_rate || 0) * 100)}% · B {Math.round((e.stats?.b?.reply_rate || 0) * 100)}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
